import {
  isLaborLineNonEmpty,
  isPartLineNonEmpty,
} from './buildClaimGenerationPayload';
import {
  getClaimRequests,
  normalizeRepairOrderDateToMdy,
  type ClaimLaborLineRow,
  type ClaimPartLineRow,
  type ClaimRepairEntry,
} from './claimIntakeUtils';
import {
  parseNumericField,
  type CustomerAddressFields,
  type ServicerAddressFields,
} from './buildUnifiedClaimPayload';
import { vehicleNotesToPayload, type VehicleNotesFormState } from './vehicleNotesUtils';
import type { AdditionalSourceItem } from './additionalInformationUpload';

/** Default line shape when the user adds a row after 3csmapper (matches review editor). */
const DEFAULT_LABOR_LINE: Record<string, unknown> = {
  labor_id: 0,
  labor_description: '',
  labor_opcode: '',
  labor_hours: 0,
  amount: 0,
  loss_code: '',
  loss_code_description: '',
  component: '',
};

const DEFAULT_PART_LINE: Record<string, unknown> = {
  part_id: 0,
  part_description: '',
  part_number: '',
  part_quantity: 0,
  unit_cost: 0,
  amount: 0,
  loss_code: '',
  loss_code_description: '',
  component: '',
};

export type ClaimIntakeFormEdits = Readonly<{
  repairs: ClaimRepairEntry[];
  laborRate: string;
  mileageIn?: string;
  invoiceNumber?: string;
  repairOrderDate?: string;
  arrivalMethod?: string;
  atServiceCenter?: string;
  reimbursement?: string;
  paymentEmail?: string;
  vehicleNotes?: VehicleNotesFormState;
  additionalSources?: AdditionalSourceItem[];
  /** Primary document uploads — written as root `sources` (replaces source_id/file_name). */
  sources?: AdditionalSourceItem[];
  customerName?: string;
  customerPhone?: string;
  customerAddress?: CustomerAddressFields;
  servicerName?: string;
  servicerPhone?: string;
  servicerContactName?: string;
  servicerMobile?: string;
  wantText?: string;
  contactEmail?: string;
  servicerAddress?: ServicerAddressFields;
  dealerNumber?: string;
}>;

function pickNonEmptyScalar(ui?: string, parser?: string): string {
  return ui?.trim() ? ui : parser?.trim() ?? ui ?? '';
}

function repairsHaveContent(repairs: ClaimRepairEntry[]): boolean {
  return repairs.some(
    (r) =>
      Boolean(r.complaint.trim() || r.cause.trim() || r.correction.trim()) ||
      r.labor_lines.some(isLaborLineNonEmpty) ||
      r.part_lines.some(isPartLineNonEmpty)
  );
}

function pickAddressFields(
  ui?: CustomerAddressFields,
  parser?: CustomerAddressFields
): CustomerAddressFields | undefined {
  if (!ui && !parser) return undefined;
  return {
    address1: pickNonEmptyScalar(ui?.address1, parser?.address1),
    address2: pickNonEmptyScalar(ui?.address2, parser?.address2),
    city: pickNonEmptyScalar(ui?.city, parser?.city),
    state: pickNonEmptyScalar(ui?.state, parser?.state),
    postalCode: pickNonEmptyScalar(ui?.postalCode, parser?.postalCode),
    countryCode: pickNonEmptyScalar(ui?.countryCode, parser?.countryCode),
  };
}

/** Prefer live UI values; fall back to estimateparser values for empty fields. */
export function mergeClaimFormEditsPreferUi(
  parserEdits: ClaimIntakeFormEdits,
  uiEdits: ClaimIntakeFormEdits
): ClaimIntakeFormEdits {
  return {
    repairs: repairsHaveContent(uiEdits.repairs) ? uiEdits.repairs : parserEdits.repairs,
    laborRate: pickNonEmptyScalar(uiEdits.laborRate, parserEdits.laborRate),
    mileageIn: pickNonEmptyScalar(uiEdits.mileageIn, parserEdits.mileageIn),
    invoiceNumber: pickNonEmptyScalar(uiEdits.invoiceNumber, parserEdits.invoiceNumber),
    repairOrderDate: pickNonEmptyScalar(uiEdits.repairOrderDate, parserEdits.repairOrderDate),
    arrivalMethod: pickNonEmptyScalar(uiEdits.arrivalMethod, parserEdits.arrivalMethod),
    atServiceCenter: pickNonEmptyScalar(uiEdits.atServiceCenter, parserEdits.atServiceCenter),
    reimbursement: pickNonEmptyScalar(uiEdits.reimbursement, parserEdits.reimbursement),
    paymentEmail: pickNonEmptyScalar(uiEdits.paymentEmail, parserEdits.paymentEmail),
    vehicleNotes: uiEdits.vehicleNotes ?? parserEdits.vehicleNotes,
    additionalSources: uiEdits.additionalSources ?? parserEdits.additionalSources,
    sources: uiEdits.sources ?? parserEdits.sources,
    customerName: uiEdits.customerName,
    customerPhone: uiEdits.customerPhone,
    customerAddress: uiEdits.customerAddress,
    servicerName: pickNonEmptyScalar(uiEdits.servicerName, parserEdits.servicerName),
    servicerPhone: pickNonEmptyScalar(uiEdits.servicerPhone, parserEdits.servicerPhone),
    servicerContactName: pickNonEmptyScalar(uiEdits.servicerContactName, parserEdits.servicerContactName),
    servicerMobile: pickNonEmptyScalar(uiEdits.servicerMobile, parserEdits.servicerMobile),
    wantText: pickNonEmptyScalar(uiEdits.wantText, parserEdits.wantText),
    contactEmail: uiEdits.contactEmail,
    servicerAddress: pickAddressFields(uiEdits.servicerAddress, parserEdits.servicerAddress),
    dealerNumber: pickNonEmptyScalar(uiEdits.dealerNumber, parserEdits.dealerNumber),
  };
}

function cloneClaimObject(claimObject: Record<string, unknown>): Record<string, unknown> {
  return structuredClone(claimObject);
}

function mergeRecord(
  existing: unknown,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const base =
    existing != null && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...(existing as Record<string, unknown>) }
      : {};
  return { ...base, ...patch };
}

function mergeAddress(
  existing: unknown,
  fields: CustomerAddressFields | ServicerAddressFields
): Record<string, unknown> {
  const base =
    existing != null && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...(existing as Record<string, unknown>) }
      : {};
  return {
    ...base,
    address1: fields.address1.trim(),
    address2: fields.address2.trim(),
    city: fields.city.trim(),
    state: fields.state.trim(),
    postal_code: fields.postalCode.trim(),
    country_code: fields.countryCode.trim(),
  };
}

function mergeLaborLineFromDraft(
  existing: Record<string, unknown> | undefined,
  draft: ClaimLaborLineRow,
  laborRate: string
): Record<string, unknown> {
  const hours = parseNumericField(draft.labor_hours);
  const rate = parseNumericField(laborRate);
  const amountFromDraft =
    draft.allowFixedValue || draft.labor_amount.trim()
      ? parseNumericField(draft.labor_amount)
      : hours * rate;
  return mergeRecord(existing ?? DEFAULT_LABOR_LINE, {
    labor_description: draft.labor_description.trim(),
    labor_opcode: draft.labor_op_code.trim(),
    labor_hours: hours,
    amount: amountFromDraft,
  });
}

function mergePartLineFromDraft(
  existing: Record<string, unknown> | undefined,
  draft: ClaimPartLineRow
): Record<string, unknown> {
  const qty = parseNumericField(draft.part_quantity);
  const unitCost = parseNumericField(draft.part_unit_price);
  const amountFromDraft = draft.part_amount.trim()
    ? parseNumericField(draft.part_amount)
    : qty * unitCost;
  return mergeRecord(existing ?? DEFAULT_PART_LINE, {
    part_description: draft.part_description.trim(),
    part_number: draft.part_number.trim(),
    part_quantity: qty,
    unit_cost: unitCost,
    amount: amountFromDraft,
  });
}

function mergeRequestFromRepairDraft(
  existing: Record<string, unknown> | undefined,
  repair: ClaimRepairEntry,
  laborRate: string
): Record<string, unknown> {
  const req = existing ? { ...existing } : { parts: [], labor: [] };

  req.complaint_description = repair.complaint.trim();
  req.cause_description = repair.cause.trim();
  req.corrective_action_description = repair.correction.trim();
  if (repair.tiretread.trim()) {
    req.tiretread = parseNumericField(repair.tiretread);
  } else {
    delete req.tiretread;
  }

  const existingParts = Array.isArray(req.parts)
    ? (req.parts as Record<string, unknown>[])
    : [];
  const existingLabor = Array.isArray(req.labor)
    ? (req.labor as Record<string, unknown>[])
    : [];

  const partDraft = repair.part_lines.filter(isPartLineNonEmpty);
  const laborDraft = repair.labor_lines.filter(isLaborLineNonEmpty);

  req.parts = partDraft.map((draft, idx) => mergePartLineFromDraft(existingParts[idx], draft));
  req.labor = laborDraft.map((draft, idx) =>
    mergeLaborLineFromDraft(existingLabor[idx], draft, laborRate)
  );

  return req;
}

function applyTripAndInvoiceEdits(base: Record<string, unknown>, edits: ClaimIntakeFormEdits): void {
  if (edits.mileageIn !== undefined) base.failure_odometer = edits.mileageIn.trim();
  if (edits.invoiceNumber !== undefined) base.invoice_number = edits.invoiceNumber.trim();
  if (edits.repairOrderDate !== undefined) {
    const d = normalizeRepairOrderDateToMdy(edits.repairOrderDate);
    base.failure_date = d;
    base.repair_order_date = d;
  }
  if (edits.laborRate !== undefined) base.labor_rate = parseNumericField(edits.laborRate);
  if (edits.reimbursement !== undefined) base.reimbursement_method = edits.reimbursement;
  const isCreditCard =
    edits.reimbursement === 'CONNEXPAY' ||
    (edits.reimbursement === undefined && base.reimbursement_method === 'CONNEXPAY');
  if (isCreditCard) {
    if (edits.paymentEmail !== undefined) {
      base.payment_email = edits.paymentEmail.trim();
    }
  } else if (edits.reimbursement !== undefined || edits.paymentEmail !== undefined) {
    delete base.payment_email;
  }
  if (edits.arrivalMethod !== undefined) base.vehicle_arrival_method = edits.arrivalMethod;
  if (edits.atServiceCenter !== undefined) base.vehicle_at_service_center = edits.atServiceCenter;
  if (edits.vehicleNotes !== undefined) {
    base.vehicle_notes = vehicleNotesToPayload(edits.vehicleNotes);
  }
  if (edits.additionalSources !== undefined) {
    base.additional_sources = edits.additionalSources;
  }
  if (edits.sources !== undefined) {
    delete base.source_id;
    delete base.file_name;
    delete base.file_sources;
    if (edits.sources.length > 0) {
      base.sources = edits.sources;
    } else {
      delete base.sources;
    }
  }
}

function applyCustomerEdits(base: Record<string, unknown>, edits: ClaimIntakeFormEdits): void {
  if (edits.customerName === undefined || edits.customerPhone === undefined || !edits.customerAddress) {
    return;
  }
  const existingCustomer = base.customer;
  base.customer = mergeRecord(existingCustomer, {
    name: edits.customerName.trim(),
    phone_no: edits.customerPhone.trim(),
    address: mergeAddress(
      existingCustomer != null &&
        typeof existingCustomer === 'object' &&
        !Array.isArray(existingCustomer)
        ? (existingCustomer as Record<string, unknown>).address
        : undefined,
      edits.customerAddress
    ),
  });
}

function applyServicerEdits(base: Record<string, unknown>, edits: ClaimIntakeFormEdits): void {
  if (edits.servicerName === undefined) return;
  const existingServicer = base.servicer;
  const servicerPatch: Record<string, unknown> = { name: edits.servicerName.trim() };
  if (edits.contactEmail !== undefined) servicerPatch.email = edits.contactEmail.trim();
  if (edits.servicerPhone !== undefined) servicerPatch.phone_no = edits.servicerPhone.trim();
  if (edits.servicerContactName !== undefined) servicerPatch.contact_name = edits.servicerContactName.trim();
  if (edits.servicerMobile !== undefined) servicerPatch.mobile_no = edits.servicerMobile.trim();
  if (edits.wantText !== undefined) servicerPatch.want_text = edits.wantText.trim();
  if (edits.dealerNumber !== undefined) servicerPatch.dealer_no = edits.dealerNumber.trim();
  if (edits.servicerAddress) {
    servicerPatch.address = mergeAddress(
      existingServicer != null &&
        typeof existingServicer === 'object' &&
        !Array.isArray(existingServicer)
        ? (existingServicer as Record<string, unknown>).address
        : undefined,
      edits.servicerAddress
    );
  }
  base.servicer = mergeRecord(existingServicer, servicerPatch);
}

/**
 * Merges user-editable form values into the 3csmapper claim object.
 * Preserves all other keys/fields from the API response (policies, product, loss codes, etc.).
 */
export function mergeClaimObjectWithFormEdits(
  claimObject: Record<string, unknown>,
  edits: ClaimIntakeFormEdits
): Record<string, unknown> {
  const base = cloneClaimObject(claimObject);
  applyTripAndInvoiceEdits(base, edits);
  applyCustomerEdits(base, edits);
  applyServicerEdits(base, edits);

  const existingRequests = getClaimRequests(base);
  const mergedRequests = edits.repairs.map((repair, repairIdx) =>
    mergeRequestFromRepairDraft(existingRequests[repairIdx], repair, edits.laborRate)
  );
  base.requests = mergedRequests;

  return base;
}

/** @deprecated Prefer mergeClaimObjectWithFormEdits — kept for call sites that only sync repairs. */
export function syncClaimObjectWithRepairs(
  claimObject: Record<string, unknown>,
  repairs: ClaimRepairEntry[],
  laborRate: string
): Record<string, unknown> {
  return mergeClaimObjectWithFormEdits(claimObject, { repairs, laborRate });
}
